/**
 * Today on a day that is over (1.5.13 §1b) — a record of what happened: every visit on record for that day, the
 * ones booked in the app (with how they were closed out) and the ones from before it, one card per apartment in
 * time order, each with its status in words and who closed it; the count line "n visits · n served · n cancelled ·
 * n no-show". Nothing here can be booked, moved or finished — the only press on a card is Files, on a booked visit
 * that was served (a row from before the app has no folder the files feature can address).
 *
 * The crew sees the day too: only the visits they closed out themselves, with nothing to press.
 *
 * DayNav is the way there and back, on Today itself and here: a day to go to (the calendar) and "Back to today"
 * (1.5.17: no arrow buttons; Alt+← / Alt+→ on a keyboard). It never goes past today — the days ahead are Look
 * ahead's and the schedule's.
 */
import { useEffect, useId, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { TodayVisit } from '@shared/api-types';
import type { PastCounts, PastStatus } from '@shared/past-day';
import { periodAt } from '@shared/visits';
import { addDaysToKey, isDateKey, isWeekdayKey, keyToIsoDay } from '@shared/dates';
import { formatDayKey, formatDayMedium, formatDayShort, formatInstant } from '@/lib/dates';
import { useToday } from '@/lib/queries';
import { usePositionTimes } from '@/lib/useTimes';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { STATUS_STYLE, type StatusKey } from '@/components/StatusBadge';
import { Updated } from '@/components/Updated';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { VisitFilesButton } from '@/features/files/VisitFiles';
import type { Me } from '@/auth/useMe';

/** The nearest weekday before (-1) or after (+1) a day. */
export function weekdayBeside(key: string, dir: -1 | 1): string {
  let k = addDaysToKey(key, dir);
  while (!isWeekdayKey(k)) k = addDaysToKey(k, dir);
  return k;
}

/** Whether a key press is a person typing: a field, a list, an editable box — never the day's own date. */
function typingIn(el: EventTarget | null, own: HTMLElement | null): boolean {
  if (!(el instanceof HTMLElement) || el === own) return false;
  if (el instanceof HTMLInputElement) return !['checkbox', 'radio', 'button', 'submit', 'reset'].includes(el.type);
  return el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement || !!el.closest('[contenteditable]:not([contenteditable="false"])');
}

/**
 * The day to go to (the calendar, today or before) · Back to today. `day` is the day showing; nothing goes past today.
 * 1.5.17: the two arrow buttons are gone on every width — "somebody can just tap the calendar". A keyboard keeps
 * Alt+← (the weekday before) and Alt+→ (the weekday after, never past today) while the day is showing: not while a
 * person types in a field, not while a dialog is open. The page says the day it lands on, as it does on any load.
 */
export function DayNav({ day, today }: { day: string; today: string }) {
  const { t } = useTranslation();
  const [, setParams] = useSearchParams();
  const uid = useId();
  const input = useRef<HTMLInputElement | null>(null);
  const go = (k: string) => setParams(k >= today ? {} : { day: k }, { replace: false });
  const goRef = useRef(go);
  goRef.current = go;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
      if (typingIn(e.target, input.current) || document.querySelector('[role="dialog"]')) return;
      // ours: the browser's own Alt+← (back a page) must not also happen
      e.preventDefault();
      if (e.key === 'ArrowLeft') goRef.current(weekdayBeside(day, -1));
      else if (day < today) goRef.current(weekdayBeside(day, 1));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [day, today]);
  return (
    <div className="chips range-nav" role="group" aria-label={t('today.dayNav')} data-testid="day-nav">
      <label htmlFor={`${uid}-day`} className="sr-only">
        {t('day17.pick')}
      </label>
      <input
        ref={input}
        id={`${uid}-day`}
        type="date"
        className="input day-pick"
        style={{ width: 'auto', minHeight: 44, padding: '4px 8px' }}
        value={keyToIsoDay(day)}
        max={keyToIsoDay(today)}
        aria-keyshortcuts="Alt+ArrowLeft Alt+ArrowRight"
        onChange={(e) => {
          const k = e.target.value.replace(/-/g, '');
          if (isDateKey(k)) go(k);
        }}
        data-testid="day-pick"
      />
      {day < today ? (
        <Button size="sm" onClick={() => go(today)} data-testid="day-today">
          {t('today.backToToday')}
        </Button>
      ) : null}
    </div>
  );
}

/** The badge a past status wears: the same soft pairs as everywhere, always an icon and the words. */
const BADGE: Record<PastStatus, StatusKey> = {
  served: 'completed',
  cancelledTenant: 'cancelled',
  cancelledOffice: 'cancelled',
  cancelled: 'cancelled',
  noShow: 'noShow',
  unableToReach: 'unable',
  unableToComplete: 'unable',
  rescheduled: 'rescheduled',
  notConfirmed: 'proposed',
  notRecorded: 'scheduled',
};

export function countLine(t: TFunction, c: PastCounts): string {
  return [t('today.past.visits', { count: c.visits }), t('today.past.served', { count: c.served }), t('today.past.cancelled', { count: c.cancelled }), t('today.past.noShow', { count: c.noShow })].join(' · ');
}

export function PastDayPage({ me, day, today }: { me: Me; day: string; today: string }) {
  const { t, i18n } = useTranslation();
  const announce = useAnnounce();
  const q = useToday(day);
  // 1.6.1: the period beside a visit number is the one of the time shown (the old record's own time, else the day
  // plan's for that visit), not the imported column
  const { label, period } = usePositionTimes();
  const isCrew = me.role === 'crew';
  const seesFiles = me.role === 'admin' || me.role === 'coadmin' || me.role === 'scheduler';
  const visits = q.data?.past ? q.data.visits : [];
  const counts = q.data?.past ? (q.data.counts ?? null) : null;
  const dayWords = `${formatDayKey(day, i18n.language)}, ${day.slice(0, 4)}`;

  // the day is said once it has loaded (a screen reader hears what the page now shows)
  const said = useRef<string | null>(null);
  useEffect(() => {
    if (!q.data?.past || said.current === day) return;
    said.current = day;
    const c = q.data.counts;
    announce(c && c.visits ? t('today.past.loaded', { day: formatDayMedium(day, i18n.language), line: countLine(t, c) }) : `${formatDayMedium(day, i18n.language)}: ${t(isCrew ? 'today.past.emptyCrew' : 'today.past.empty')}`);
  }, [q.data, day, announce, t, i18n.language, isCrew]);

  const card = (v: TodayVisit) => {
    const r = v.record!;
    const style = STATUS_STYLE[BADGE[r.status]];
    const time = r.time ?? label(day, v.position);
    const words = r.status === 'rescheduled' && r.rescheduledToKey ? t('today.past.rescheduledTo', { day: formatDayShort(r.rescheduledToKey, i18n.language) }) : t(`today.past.status.${r.status}`);
    return (
      <li key={v.appointmentId} className="visit is-done" data-testid={`past-${v.appointmentId}`} data-status={r.status}>
        <div className="time tnum">
          {time ?? '—'}
          <small>
            {v.position ? t('today.positionN', { n: v.position }) : ''}
            {v.position && v.servicePeriod ? ' · ' : ''}
            {t(`period.${r.time ? (periodAt(r.minutes) ?? v.servicePeriod) : period(day, v.position, v.servicePeriod)}`, { defaultValue: v.servicePeriod })}
          </small>
        </div>
        <div className="who">
          <div className="apt">
            {v.unit} <span>· {v.preferredName ? `${v.preferredName} (${v.tenantName})` : v.tenantName}</span>
          </div>
          <div className="sub">
            <span>{v.kind === 'facility' ? (v.locationDetails ?? t('status.facility')) : v.program === 'TECH' ? t('techBook.title') : t('programs.housekeeping')}</span>
            {r.source === 'airtable' ? <span data-testid={`past-source-${v.appointmentId}`}>{[t('history.fromAirtable'), r.deepClean ? t('history.airtableDeep') : null].filter(Boolean).join(' · ')}</span> : null}
          </div>
          {r.closedBy ? (
            <div className="closed-line" data-testid={`past-closed-${v.appointmentId}`}>
              {r.closedAt ? t('today.past.closedByAt', { by: r.closedBy, when: formatInstant(r.closedAt, i18n.language) }) : t('today.past.closedBy', { by: r.closedBy })}
            </div>
          ) : null}
        </div>
        <div className="side">
          <div className="badges">
            <span className={`status has-icon ${style.tone}`} data-status={r.status}>
              <span className="sdot" aria-hidden="true" />
              <Icon name={style.icon} />
              {words}
            </span>
          </div>
          {seesFiles && r.source === 'app' && r.status === 'served' && v.kind === 'tenant' && v.program.toUpperCase() === 'HOUSEKEEPING' ? (
            <div className="row-actions">
              <VisitFilesButton kind="housekeeping" id={v.appointmentId} compact label={t('files.forVisit', { day: formatDayMedium(day, i18n.language) })} />
            </div>
          ) : null}
        </div>
      </li>
    );
  };

  return (
    <section aria-labelledby="h-today" data-testid="past-day-page">
      <div className="page-head">
        <div>
          <div className="eyebrow">{t('today.past.eyebrow')}</div>
          <h1 id="h-today" tabIndex={-1}>
            {dayWords}
          </h1>
        </div>
        <div className="head-side">
          <div className="head-actions">
            <DayNav day={day} today={today} />
            <Updated at={q.dataUpdatedAt} fetching={q.isFetching && !q.isLoading} />
          </div>
        </div>
      </div>

      <div className="warn" style={{ marginBottom: 14 }} data-testid="past-note">
        <Icon name="info" />
        <div>{t(isCrew ? 'today.past.noteCrew' : 'today.past.note')}</div>
      </div>

      <Card aria-labelledby="h-visits" aria-busy={q.isLoading}>
        <CardHeader>
          <CardTitle id="h-visits">{t(isCrew ? 'today.past.titleCrew' : 'today.past.title')}</CardTitle>
          {counts ? (
            <span className="help" data-testid="past-counts">
              {countLine(t, counts)}
            </span>
          ) : null}
        </CardHeader>
        {q.isLoading ? (
          <CardContent>
            <p className="help">{t('app.loading')}</p>
          </CardContent>
        ) : q.isError ? (
          <CardContent>
            <div className="warn take" role="alert">
              <Icon name="alert" />
              <div>
                <b>{t('today.past.error')}</b>
                <div>{(q.error as Error).message}</div>
                <div className="row">
                  <Button size="sm" onClick={() => q.refetch()}>
                    {t('today.retry')}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        ) : (
          <>
            {q.data?.historyComplete === false ? (
              <div className="warn" style={{ margin: '0 18px 12px' }} role="status" data-testid="past-gathering">
                <Icon name="clock" />
                <div>
                  {t('today.past.gathering')}{' '}
                  <Button size="sm" variant="secondary" onClick={() => q.refetch()}>
                    {t('today.retry')}
                  </Button>
                </div>
              </div>
            ) : null}
            {visits.length === 0 ? (
              <div className="empty" data-testid="past-empty">
                <Icon name="calendarEmpty" />
                <b>{t(isCrew ? 'today.past.emptyCrew' : 'today.past.empty')}</b>
              </div>
            ) : (
              <ul aria-labelledby="h-visits" data-testid="past-day">
                {visits.map(card)}
              </ul>
            )}
          </>
        )}
      </Card>
    </section>
  );
}
